import { useReducedMotion } from "framer-motion";
import { PALETTE, Pine } from "./sceneSeason";
import { HOLIDAYS, resolveTheme } from "./sceneHoliday";
import { useSite } from "../../lib/site";

const ease = (t) => t * t * (3 - 2 * t);
const FADE = { background: `linear-gradient(to bottom, ${Array.from({ length: 11 }, (_, i) => `hsl(var(--background) / ${ease(i / 10).toFixed(3)}) ${(82 + 15 * (i / 10)).toFixed(1)}%`).join(", ")})` };

export const Lift = ({ children }) => <g transform="translate(0 -16)">{children}</g>;

const cloud = (x, y, s, fill) => <g transform={`translate(${x} ${y}) scale(${s})`} fill={fill}><ellipse cx="0" cy="0" rx="12" ry="5.5" /><ellipse cx="-6" cy="-3" rx="6" ry="5" /><ellipse cx="5" cy="-4" rx="7" ry="6" /></g>;

export function useSceneTheme() {
  const site = useSite();
  const theme = resolveTheme(site.scene_theme, site.scene_holidays);
  const hol = theme.holiday ? HOLIDAYS[theme.holiday] : {};
  return { ...theme, night: !!hol.night, pal: { ...PALETTE[theme.season], ...(hol.sky ? { sky: hol.sky } : {}), ...(hol.cloud ? { cloud: hol.cloud } : {}) } };
}

export default function MiniScene({ testid, pines = [], somber, children }) {
  const { season, pal, night } = useSceneTheme();
  const reduce = useReducedMotion();
  const id = `${testid}-sky`;
  return (
    <div className="absolute inset-x-0 top-0 pointer-events-none select-none" style={{ height: "var(--band)", background: `linear-gradient(to bottom, ${pal.sky[0]} 50%, hsl(var(--background)) 50%)` }} data-testid={testid} data-season={season} aria-hidden>
      <svg viewBox="-70 -26 380 191" preserveAspectRatio="xMidYMax meet" className="absolute inset-0 h-full w-full dark:[filter:brightness(0.85)_saturate(0.92)]">
        <defs><linearGradient id={id} gradientUnits="userSpaceOnUse" x1="0" y1="-26" x2="0" y2="134"><stop offset="0" stopColor={pal.sky[0]} /><stop offset="0.65" stopColor={pal.sky[1]} /><stop offset="1" stopColor={pal.sky[2]} /></linearGradient></defs>
        <rect x="-80" y="-60" width="400" height="230" fill={`url(#${id})`} />
        {night
          ? <g>{[[30, 20], [70, 34], [120, 14], [168, 30], [214, 18], [96, 46], [190, 50]].map(([x, y]) => <circle key={x} cx={x} cy={y} r="0.9" fill="#fff" opacity="0.8" />)}<circle cx="200" cy="28" r="8" fill="#FFF1C4" /></g>
          : <g><circle cx="200" cy="28" r="16" fill={pal.sun} opacity="0.25" /><circle cx="200" cy="28" r="7.5" fill={pal.sun} /></g>}
        {cloud(-30, 0, 1, pal.cloud)}{cloud(54, 30, 0.9, pal.cloud)}{cloud(150, 4, 0.7, pal.cloud)}{cloud(262, 40, 0.8, pal.cloud)}
        {season === "winter" && !reduce && Array.from({ length: 30 }, (_, i) => (
          <circle key={i} cx={((i * 53) % 380) - 70} cy="-54" r={0.9 + (i % 3) * 0.4} fill="#fff" opacity="0.85">
            <animateTransform attributeName="transform" type="translate" values={`0 0;${(i % 5) - 2} 220`} dur={`${6 + (i % 4)}s`} begin={`-${(i * 0.7) % 6}s`} repeatCount="indefinite" />
          </circle>
        ))}
        <path d="M-80 134 C-40 116 -10 126 30 116 S70 128 110 118 S190 112 250 124 S300 112 320 120 V134 Z" fill={pal.hill} />
        <Lift>{[[-52, 30], [-36, 22], [262, 26], [282, 36], [298, 24]].map(([x, h]) => <Pine key={`far${x}`} x={x} h={h} snow={season === "winter"} tone={pal.pine} />)}{pines.map(([x, h]) => <Pine key={x} x={x} h={h} snow={season === "winter"} tone={pal.pine} />)}</Lift>
        <rect x="-80" y="132" width="400" height="40" fill={pal.ground} />
        <path d="M-80 133 H320" stroke={pal.edge} strokeWidth="2.2" />
        {somber && <rect x="-80" y="-60" width="400" height="230" fill="#4B5366" opacity="0.22" />}
        {children}
      </svg>
      <div className="absolute inset-x-0 top-0 -bottom-3" style={FADE} />
    </div>
  );
}

export const BAND = "round(down, min(60cqw, 400px), 2px)";
